// src/components/SharingStatusDialog.tsx
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AccessSelector } from "@/components/AccessSelector";
import type { AccessControl } from '@/lib/firebase';

interface SharingStatusDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  accessControl: AccessControl;
  onAccessChange: (accessControl: AccessControl) => void;
  userDomain?: string;
}

export function SharingStatusDialog({ 
  isOpen, 
  onOpenChange, 
  accessControl, 
  onAccessChange,
  userDomain 
}: SharingStatusDialogProps) {
  // Set initial state from props
  const [currentAccessControl, setCurrentAccessControl] = useState<AccessControl>(accessControl);
  
  // Reset current access control when dialog opens with new props
  useEffect(() => {
    if (isOpen) {
      setCurrentAccessControl(accessControl);
    }
  }, [isOpen, accessControl]);

  // Handle changes from AccessSelector
  const handleAccessChange = (newAccessControl: AccessControl) => {
    setCurrentAccessControl(newAccessControl);
  };

  // Handle save button click
  const handleSave = () => {
    onAccessChange(currentAccessControl);
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-32px)] sm:max-w-md md:max-w-lg lg:max-w-xl max-h-[95vh] flex flex-col overflow-hidden">
        <DialogHeader className="px-1 shrink-0">
          <DialogTitle>Sharing Settings</DialogTitle>
          <DialogDescription>
            Choose who can access this assistant
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden pr-3 py-2 -mr-3 min-h-0">
          <AccessSelector
            value={currentAccessControl.type}
            emails={currentAccessControl.emails}
            domains={currentAccessControl.domains}
            groups={currentAccessControl.groups}
            userDomain={userDomain}
            onChange={handleAccessChange}
          />
        </div>

        <DialogFooter className="mt-4 shrink-0 flex justify-end gap-2 flex-wrap sm:flex-nowrap border-t border-border pt-4">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            className="w-full sm:w-auto"
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}